import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Capability } from '@boud/contracts';
import { apiRequest, setAccessToken, setSessionEndedHandler } from '../api/client';
import { usePreferences } from '../lib/PreferencesProvider';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  employer_id: string;
  employer_name: string;
  is_system_chief: boolean;
  capabilities: Capability[];
  is_cross_company: boolean;
  language: 'en' | 'ar' | null;
  theme: 'light' | 'dark' | null;
}

interface AuthState {
  user: CurrentUser | null;
  /** True until the initial session-restore attempt settles. */
  isRestoring: boolean;
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * Whether the signed-in user holds a capability.
   *
   * **This is for deciding what to render, and nothing else.** FRS §4.1: the
   * client gate is coarse and the server is authoritative. Every capability
   * consulted here is enforced again server-side, with a test proving it — so a
   * user who reaches a hidden screen by editing the URL gets a 403, not data.
   */
  can: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // `AuthProvider` sits inside `QueryClientProvider` in `main.tsx`, which is
  // what makes this legal — and the ordering is load-bearing for that reason.
  const queryClient = useQueryClient();

  // Legal because `PreferencesProvider` wraps `AuthProvider` in main.tsx — the
  // toggles exist on the sign-in screen, before there is any session to save to.
  const { adopt } = usePreferences();

  /**
   * Adopting the account's saved language and theme is part of loading the user,
   * not a separate concern — which is why it happens here rather than in a
   * screen. `/me` already carries both, so restoring them costs no extra
   * request, and doing it anywhere further down would let the interface paint in
   * the wrong language first and correct itself afterwards.
   */
  const loadUser = useCallback(async () => {
    const me = await apiRequest<CurrentUser>('/me');
    setUser(me);
    adopt({ language: me.language, theme: me.theme });
  }, [adopt]);

  /**
   * Restores the session on load.
   *
   * The access token is deliberately not persisted anywhere a script can read,
   * so a page refresh always starts without one. The httpOnly refresh cookie is
   * what survives, and exchanging it here is what keeps the user signed in
   * across a reload without ever exposing a long-lived credential to the page.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL ?? '/api/v1'}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const payload = (await response.json()) as { data?: { access_token?: string } };
          if (payload.data?.access_token) {
            setAccessToken(payload.data.access_token);
            if (!cancelled) await loadUser();
          }
        }
      } catch {
        // No usable session. The login screen is the correct outcome, not an error.
      } finally {
        if (!cancelled) setIsRestoring(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  // When a refresh finally fails mid-session, drop the user rather than leaving
  // the UI rendering a session that no longer exists.
  useEffect(() => {
    setSessionEndedHandler(() => setUser(null));
    return () => setSessionEndedHandler(null);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      const session = await apiRequest<{ access_token: string }>('/auth/login', {
        method: 'POST',
        body: { email, password, remember_me: rememberMe },
      });

      /*
       * Empty the query cache **before** the new user's first request.
       *
       * The cache is keyed by what was asked for — `['calendar', 'me']`,
       * `['bookings']`, `['tickets']` — and never by *who asked*, so without
       * this every entry the previous account left behind is served to the next
       * one. `staleTime` is 30 seconds and `refetchOnWindowFocus` is off, so it
       * is not a flicker: sign out of Layla, sign in as Nourah, and Nourah's
       * Settings screen reads "Connected as layla@boud.ai" until something
       * happens to refetch it.
       *
       * Clearing on sign-out alone is not enough. A session can end without
       * anybody pressing sign out — an expired refresh token drops the user and
       * leaves the cache behind — so the guarantee has to be made here, at the
       * moment the identity actually changes.
       */
      queryClient.clear();

      setAccessToken(session.access_token);
      await loadUser();
    },
    [loadUser, queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' });
    } finally {
      // Cleared even if the call fails: the local session must end regardless,
      // and the server-side token expires on its own.
      setAccessToken(null);
      setUser(null);

      /*
       * And the cached answers go with it. Leaving them would show the next
       * person to use this browser the previous one's bookings, tickets and
       * visitors on the way in — and would leave them readable in memory to
       * anything that can reach the cache after the session is over.
       */
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isRestoring,
      signIn,
      signOut,
      refreshUser: loadUser,
      can: (capability) => user?.capabilities.includes(capability) ?? false,
    }),
    [user, isRestoring, signIn, signOut, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider.');
  return context;
}
